import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Phone, Check, Loader2, ArrowRight, MessageCircle, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const PREVIEW_COUNT = 6;

function fmtRel(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffMin = Math.round((d - new Date()) / 60000);
    if (diffMin <= 0) {
      const past = -diffMin;
      if (past < 60) return `${past} min fa`;
      const h = Math.floor(past / 60);
      if (h < 24) return `${h}h fa`;
      return `${Math.floor(h / 24)}g fa`;
    }
    if (diffMin < 60) return `tra ${diffMin} min`;
    return `tra ${Math.floor(diffMin / 60)}h`;
  } catch { return iso; }
}

export default function FollowupDashboardPanel() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "operatore";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/followups", { params: { status: "pending", overdue_only: true, limit: 50 } });
      setItems(data || []);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [load]);

  const handleDone = async (id, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setWorking(id);
    try {
      await api.post(`/followups/${id}/done`, { note: "" });
      toast.success("Promemoria chiuso");
      setItems((arr) => arr.filter((it) => it.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setWorking(null);
    }
  };

  // Hide panel when loading first time + empty (avoid layout flicker on empty state)
  if (loading) return null;
  if (items.length === 0) return null;

  const preview = items.slice(0, PREVIEW_COUNT);
  const remaining = items.length - preview.length;

  return (
    <div
      className="card-shell p-4 md:p-5"
      style={{ borderLeft: "4px solid #DC2626", background: "linear-gradient(135deg, #FFFFFF 0%, #FEF2F2 100%)" }}
      data-testid="followup-dashboard-panel"
    >
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <div className="flex items-start md:items-center gap-3 min-w-0">
          <div className="p-2 rounded-lg animate-pulse shrink-0" style={{ background: "#FEE2E2" }}>
            <Phone className="w-5 h-5" style={{ color: "#991B1B" }} />
          </div>
          <div className="min-w-0">
            <h2 className="font-bold text-base md:text-lg" style={{ fontFamily: "Manrope" }}>
              Da chiamare ora
              <span className="ml-2 text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: "#DC2626", color: "white" }} data-testid="followup-panel-count">
                {items.length}
              </span>
            </h2>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              Clienti contattati su WhatsApp da richiamare. Si chiudono automaticamente se rispondono.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {preview.map((it) => (
          <Link
            key={it.id}
            to={`/clienti/${it.cliente_id}`}
            className="group flex items-start gap-2 p-3 rounded-lg bg-white border hover:shadow-md transition"
            style={{ borderColor: "var(--border)" }}
            data-testid={`followup-panel-item-${it.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate group-hover:underline">{it.cliente_nome || "Cliente"}</div>
              <div className="text-[11px] font-mono mt-0.5" style={{ color: "var(--text-muted)" }}>
                {it.cliente_telefono || "—"}
              </div>
              {it.message_preview && (
                <div className="text-[11px] mt-1.5 truncate" style={{ color: "#374151" }} title={it.message_preview}>
                  <MessageCircle className="w-3 h-3 inline mr-1" />
                  {it.message_preview}
                </div>
              )}
              <div className="text-[10px] mt-1.5 flex items-center gap-1" style={{ color: "#991B1B" }}>
                <Clock className="w-3 h-3" /> {fmtRel(it.snoozed_until || it.due_at)}
              </div>
            </div>
            {canManage && (
              <button
                onClick={(e) => handleDone(it.id, e)}
                disabled={working === it.id}
                className="p-2.5 md:p-1.5 rounded-md text-white hover:brightness-110 disabled:opacity-50 flex-shrink-0"
                style={{ background: "#065F46" }}
                title="Segna come chiamato"
                data-testid={`followup-panel-done-${it.id}`}
              >
                {working === it.id ? <Loader2 className="w-4 h-4 md:w-3.5 md:h-3.5 animate-spin" /> : <Check className="w-4 h-4 md:w-3.5 md:h-3.5" />}
              </button>
            )}
          </Link>
        ))}
      </div>

      {remaining > 0 && (
        <div className="mt-3 text-center text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>
          E altri <b>{remaining}</b> promemoria · apri la campanella <ArrowRight className="w-3 h-3 inline" /> in alto a destra per gestirli
        </div>
      )}
    </div>
  );
}
